import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import PublicationBookListing from '../../components/ScientificPublications/PublicationBook/PublicationBookListing';
import PublicationBookForm from '../../components/ScientificPublications/PublicationBook/PublicationBookForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { ALL_ISO_CODES } from '../../data/isoCodes';
import { COUNTRIES_DATA } from '../../data/countriesData';
import { detectDuplicates } from '../../utils/duplicateChecker';
import DuplicateWarningModal from '../../components/DuplicateWarningModal';

const PublicationBook = () => {
    const today = new Date().toISOString().split('T')[0];
    const toast = useRef(null);

    const initialForm = {
        BookName: '', Publisher: '', PublishDate: today, PublishPlace: '',
        ISBN: '', Language: 'Tiếng Việt', ProjectType: '', Country: 'Việt Nam',
        EducationLevel: '', PublishNumber: '', TotalAuthors: 1, MainAuthors: 1,
        OriginalAuthors: '', MembersJSON: '[]', AttachmentUrl: ''
    };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [filters, setFilters] = useState({
        keyword: '', status: '', fromDate: null, toDate: null
    });

    const [sortConfig, setSortConfig] = useState({ key: 'BookName', direction: 'asc' });

    const [users, setUsers] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [countries, setCountries] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [members, setMembers] = useState([]);
    const [editId, setEditId] = useState(null);

    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUserId = useMemo(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                return Number(JSON.parse(userStr).Id);
            } catch (e) { return 0; }
        }
        return 0;
    }, []);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        try {
            const headers = getAuthHeaders();
            const [uRes, ptRes, elRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=selection`, { headers }),
                fetch(`${baseUrl}/projecttype`, { headers }),
                fetch(`${baseUrl}/educationlevel`, { headers })
            ]);

            if (uRes.status === 401 || ptRes.status === 401 || elRes.status === 401 ||
                uRes.status === 403 || ptRes.status === 403 || elRes.status === 403) {
                handleLogout();
                return;
            }

            if (uRes.ok) setUsers(await uRes.json());
            if (ptRes.ok) setProjectTypes(await ptRes.json());
            if (elRes.ok) setEducationLevels(await elRes.json());

        } catch (error) {
            console.error("Lỗi tải danh mục hệ thống:", error);
        }

        setCountries(COUNTRIES_DATA);

        try {
            const displayNames = new Intl.DisplayNames(['vi'], { type: 'language' });
            const formattedLanguages = ALL_ISO_CODES.map(code => {
                let nameVi = displayNames.of(code) || code;
                return { Id: code.toUpperCase(), Name: nameVi.charAt(0).toUpperCase() + nameVi.slice(1) };
            }).filter(lang => lang.Name.toLowerCase() !== lang.Id.toLowerCase()).sort((a, b) => a.Name.localeCompare(b.Name));

            setLanguages(formattedLanguages);
        } catch (error) {
            console.error("Lỗi xử lý ngôn ngữ:", error);
        }

    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (isSilent = false) => {
        if (!isSilent) setIsLoading(true);
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        if (!currentUser || !currentUser.Id) {
            handleLogout();
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/publicationbook?userId=${currentUser.Id}`,
                {
                    method: 'GET',
                    headers: getAuthHeaders()
                }
            );

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!', life: 3000 });
        } finally {
            if (!isSilent) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    useEffect(() => {
        let result = [...data];

        if (filters.keyword) {
            const kw = filters.keyword.toLowerCase();
            result = result.filter(item =>
                (item.BookName?.toLowerCase().includes(kw)) ||
                (item.OriginalAuthors?.toLowerCase().includes(kw)) ||
                (item.Publisher?.toLowerCase().includes(kw))
            );
        }

        if (filters.status) {
            result = result.filter(item => item.Status === filters.status);
        }

        if (filters.fromDate) {
            const filterFrom = new Date(filters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => {
                if (!item.PublishDate) return false;
                return new Date(item.PublishDate).getTime() >= filterFrom.getTime();
            });
        }

        if (filters.toDate) {
            const filterTo = new Date(filters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => {
                if (!item.PublishDate) return false;
                return new Date(item.PublishDate).getTime() <= filterTo.getTime();
            });
        }

        if (sortConfig !== null) {
            result.sort((a, b) => {
                if (sortConfig.key === 'PublishDate') {
                    const dateA = a.PublishDate ? new Date(a.PublishDate).getTime() : 0;
                    const dateB = b.PublishDate ? new Date(b.PublishDate).getTime() : 0;
                    if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                }
            });
        }

        setFilteredData(result);
    }, [data, filters, sortConfig]);

    useEffect(() => {
        if (data && data.length > 0) {
            const groups = detectDuplicates(data);
            setDuplicateGroups(groups);
        } else {
            setDuplicateGroups([]);
        }
    }, [data]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/publicationbook?id=${id}&status=${encodeURIComponent(newStatus)}&userId=${userId}`, {
                method: 'PATCH',
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? "Gửi yêu cầu duyệt thành công!" : "Hủy yêu cầu duyệt thành công!",
                    life: 3000
                });
                fetchData(true);
            } else {
                const resData = await response.json();
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: resData.message || "Thao tác thất bại!", life: 3000 });
            }
        } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 }); }
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Lưu nháp') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;

            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/publicationbook?id=${id}&status=${encodeURIComponent(newStatus)}&userId=${userId}`, {
                    method: 'PATCH',
                    headers: getAuthHeaders()
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editId) {
            const currentItem = data.find(d => d.Id === editId);
            if (currentItem?.Status === 'Đã gửi duyệt' || currentItem?.Status === 'Đã duyệt') {
                toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Dữ liệu đã khóa, không thể lưu!', life: 3000 });
                return;
            }
        }

        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        if (!currentUser || !currentUser.Id) {
            return handleLogout();
        }

        if (members.length === 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng thêm ít nhất một thành viên!', life: 3000 });
        }

        const payload = {
            ...formData,
            UserId: currentUser.Id,
            MembersJSON: JSON.stringify(members),
            Id: editId ? editId : 0
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/publicationbook`, {
                method: editId ? 'PUT' : 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            let result;
            try {
                result = await response.json();
            } catch (err) {
                result = { status: 'error', message: 'Lỗi máy chủ không xác định!' };
            }

            if (response.ok && result.status !== 'error') {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: editId ? 'Cập nhật thành công!' : 'Thêm mới thành công!', life: 3000 });
                fetchData(true);
                closeModal();
            } else {
                const errorMsg = result.message || 'Lưu thất bại!';
                toast.current?.show({ severity: 'error', summary: 'Không thể lưu', detail: errorMsg, life: 6000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleEdit = async (item) => {
        let parsedMembers = [];
        try {
            parsedMembers = JSON.parse(item.MembersJSON || '[]');
        } catch (e) {
            parsedMembers = [];
        }

        const missingIds = parsedMembers
            .map(m => m.userId || m.UserId || m.Id || m.id)
            .filter(id => id && !users.find(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, {
                    headers: getAuthHeaders()
                });

                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prevUsers => {
                        const merged = [...prevUsers];
                        extraUsers.forEach(eu => {
                            if (!merged.find(u => u.Id === eu.Id)) {
                                merged.push(eu);
                            }
                        });
                        return merged;
                    });
                }
            } catch (error) { console.error("Lỗi tải thông tin thành viên bổ sung:", error); }
        }

        setEditId(item.Id);
        setFormData({ ...initialForm, ...item });
        setMembers(parsedMembers);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa hồ sơ đã gửi hoặc đã duyệt!', life: 3000 });

        confirmDeleteDialog({
            header: 'Xác nhận xóa', message: 'Bạn có chắc chắn muốn xóa sách/giáo trình này?',
            accept: async () => {
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/publicationbook?id=${id}&userId=${userId}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (res.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa bản ghi!' });
                        fetchData(true);
                    } else {
                        const err = await res.json();
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Xóa thất bại!' });
                    }
                } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' }); }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} sách/giáo trình đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;

                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/publicationbook?id=${id}&userId=${userId}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(initialForm); setMembers([]); setEditId(null); };

    const exportToExcel = () => {
        const dataToExport = filteredData;

        if (dataToExport.length === 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const excelData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Tên Sách/Giáo trình': item.BookName || '',
            'Tác giả nguyên bản': item.OriginalAuthors || '',
            'Nhà xuất bản': item.Publisher || '',
            'Năm xuất bản': item.PublishDate ? new Date(item.PublishDate).toLocaleDateString('vi-VN') : '',
            'ISBN': item.ISBN || '',
            'Loại công trình': item.ProjectType || '',
            'Ngôn ngữ': item.Language || '',
            'Cấp đào tạo': item.EducationLevel || '',
            'Quốc gia': item.Country || '',
            'Trạng thái': item.Status || 'Lưu nháp',
            'Người tạo': item.CreatorName || item.CreatorEmail || ''
        }));

        excelData.push({
            'STT': '', 'Tên Sách/Giáo trình': 'TỔNG CỘNG:', 'Tác giả nguyên bản': `${dataToExport.length} Sách/Giáo trình`,
            'Nhà xuất bản': '', 'Năm xuất bản': '', 'ISBN': '', 'Loại công trình': '', 'Ngôn ngữ': '',
            'Cấp đào tạo': '', 'Quốc gia': '', 'Trạng thái': '', 'Người tạo': ''
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "DanhSachSachGiaoTrinh");

        const wscols = [{ wch: 5 }, { wch: 45 }, { wch: 30 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 25 }];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, `DanhSachSachGiaoTrinh_${today}.xlsx`);
    };

    const handleAddNew = () => {
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        const detailedUser = users.find(u => u.Id === currentUser.Id) || {};

        setMembers([{
            userId: currentUser.Id || '',
            fullName: `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim(),
            role: 'Chủ biên',
            department: detailedUser.DepartmentName || currentUser.DepartmentName || '',
            isStudent: false,
            isInstructor: false
        }]);

        setEditId(null);
        setFormData(initialForm);
        setIsModalOpen(true);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ SÁCH - GIÁO TRÌNH</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={handleAddNew} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>

                {duplicateGroups.length > 0 && (
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#e74c3c', margin: 0, animation: 'pulse 2s infinite' }}
                        onClick={() => setIsDuplicateModalOpen(true)}
                    >
                        <i className="fa-solid fa-triangle-exclamation"></i> Phát hiện {duplicateGroups.length} nhóm trùng lặp
                    </button>
                )}
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#555' }}>DANH SÁCH CÔNG TRÌNH SÁCH/GIÁO TRÌNH CỦA TÔI</p>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                    flexWrap: isDesktop ? 'nowrap' : 'wrap',
                    gap: '15px'
                }}>
                    <div className="search-wrapper" style={{ position: 'relative', flex: isDesktop ? 'none' : '1 1 250px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input
                            type="text"
                            name="keyword"
                            placeholder="Tìm kiếm"
                            className="form-input"
                            style={{ paddingLeft: '35px', width: '100%' }}
                            value={filters.keyword}
                            onChange={handleFilterChange}
                        />
                    </div>

                    <select
                        name="status"
                        className="form-input"
                        style={{ flex: isDesktop ? 'none' : '1 1 200px' }}
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">Tất cả trạng thái</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày (Năm xuất bản)"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày (Năm xuất bản)"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>
                </div>

                {(filters.keyword || filters.status || filters.fromDate || filters.toDate) && (
                    <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            className="btn-add-new"
                            style={{ backgroundColor: '#95a5a6', margin: 0 }}
                            onClick={() => setFilters({ keyword: '', status: '', fromDate: null, toDate: null })}
                        >
                            <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                        </button>
                    </div>
                )}
            </div>

            <PublicationBookListing
                data={filteredData}
                searchQuery={filters.keyword}
                currentUserId={currentUserId}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendSubmit={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleStatusChange(id, 'Lưu nháp')}
                onBulkSend={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkStatusChange(ids, 'Lưu nháp')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <PublicationBookForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                users={users}
                languages={languages}
                countries={countries}
                projectTypes={projectTypes}
                educationLevels={educationLevels}
                members={members}
                setMembers={setMembers}
                toast={toast}
            />

            <DuplicateWarningModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                duplicateGroups={duplicateGroups}
                type="book"
                onDelete={handleDelete}
                currentUserId={currentUserId}
            />
        </div>
    );
};

export default PublicationBook;